import React, { useEffect, useState } from "react";
import { Form, FormikProvider, useFormik } from "formik";
import {
  Alert,
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
} from "@mui/material";

import { useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import GlobalDialog from "../../../components/mui/Dialogue/GlobalDialog";
import GlobalSelectField from "../../../components/mui/GlobalSelectField";
import { getTerminationData, getTerminationReasons, updateTerminationData } from "../../../redux/features/CRDslice";
import { CancelButton, SubmitButton } from "../../../components/mui/Buttons/Buttons";
import { getBatchAdmissionRegister, updateAdmissionDataAndTerminationClearance } from "../../../redux/features/batchSlice";
import Loader3D from "../../../components/Loader/Loader3D";

const AdmissionStatusLanding = ({ CourseEnrollmentId, open, setOpen, dispatchParams, loading ,batchAssignmentId}) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const { loading: crdLoading, terminationReasons, terminationData } = useSelector((state) => state.crd);
  const LOADING = crdLoading || loading;

  const [showAlert, setShowAlert] = useState(false);

  const ValidationSchema = Yup.object().shape({
    course_inactive_remarks: Yup.string().required("Please add remarks"),
    date_of_inactive: Yup.string().nullable().required("Please select inactive date"),
    course_enrollment_status: Yup.string().required("Please choose enrollment status"),
    course_restart_date: Yup.string()
      .nullable()
      .when("course_enrollment_status", {
        is: (val) => val === "2",
        then: (schema) => schema.required("Please choose restart date").nullable(),
        otherwise: (schema) => schema.nullable(),
      }),

    course_inactive_reason: Yup.string().required("Select a reason"),
    parent_remarks: Yup.string()
      .nullable()
      .when("course_enrollment_status", {
        is: (val) => val === "0",
        then: (schema) => schema.required("Please add parent remarks").nullable(),
        otherwise: (schema) => schema.nullable(),
      }),
    student_remarks: Yup.string()
      .nullable()
      .when("course_enrollment_status", {
        is: (val) => val === "0",
        then: (schema) => schema.required("Please add student remarks").nullable(),
        otherwise: (schema) => schema.nullable(),
      }),
    tutor_remarks: Yup.string()
      .nullable()
      .when("course_enrollment_status", {
        is: (val) => val === "0",
        then: (schema) => schema.required("Please add tutor remarks").nullable(),
        otherwise: (schema) => schema.nullable(),
      }),
    // student_remarks: Yup.string().required("Please add student remarks"),
    // tutor_remarks: Yup.string().required("Please add tutor remarks"),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      batch_assignment_id: batchAssignmentId,
      course_enrollment_id: CourseEnrollmentId,
      student_rating: terminationData.student_rating || null,
      student_remarks: terminationData.student_remarks || "",
      parent_rating: terminationData.parent_rating || null,
      parent_remarks: terminationData.parent_remarks || "",
      tutor_rating: terminationData.tutor_rating || null,
      tutor_remarks: terminationData.tutor_remarks || "",
      admission_fee: terminationData.admission_fee || null,
      course_fee: terminationData.course_fee || null,
      certificate: terminationData.certificate || null,
      parent_freebie: terminationData.parent_freebie || null,
      student_freebie: terminationData.student_freebie || null,
      cro_retain_call: terminationData.cro_retain_call || null,
      sro_retain_call: terminationData.sro_retain_call || null,
      cro_exit_call: terminationData.cro_exit_call || null,
      course_enrollment_status: "",
      date_of_inactive: null,
      course_inactive_reason_name: "",
      course_restart_date: null,
      course_inactive_reason: "",
      course_inactive_remarks: terminationData.course_inactive_remarks || "",
    },
    validationSchema: ValidationSchema,
    onSubmit: (values) => {
      dispatch(updateAdmissionDataAndTerminationClearance(values)).then((res) => {
        if (res.payload.status === "success") {
          enqueueSnackbar(res.payload.message, {
            variant: "success",
          });
          dispatch(getBatchAdmissionRegister(dispatchParams));
          handleCancel();
        } else {
          enqueueSnackbar(res.payload.message, {
            variant: "error",
          });
        }
      });
    },
  });

  useEffect(() => {
    if (open) {
      dispatch(getTerminationData(CourseEnrollmentId));
      dispatch(getTerminationReasons());
    }
  }, [open]);

  const handleChange = (event) => {
    const { value } = event.target;

    if (value === "0" && terminationData.certificate === 0) {
      setShowAlert(true);
    } else {
      setShowAlert(false);
    }

    formik.handleChange(event);
  };

  const handleCancel = () => {
    setOpen(false);
    formik.resetForm();
  };
  return (
    <GlobalDialog
      title={"Termination Clearance"}
      open={open}
      setOpen={setOpen}
      fullWidth
      maxWidth={"md"}
      closeButton={true}
      closeCondition={handleCancel}
      content={
        open ? (
          LOADING ? (
            <Loader3D />
          ) : (
            <>
              <Stack
                direction={{
                  xs: "column",
                  sm: "row",
                }}
                spacing={1}
                paddingBottom={3}
              >
                <Box
                  sx={{
                    backgroundImage: "linear-gradient(to right,#251351,#040926)",
                    px: "20px",
                    py: "10px",
                    borderRadius: "15px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontSize: "16px",
                  }}
                >
                  Admission No : {terminationData.admn_no}
                </Box>
                <Box
                  sx={{
                    backgroundImage: "linear-gradient(to right,#251351,#040926)",
                    px: "20px",
                    py: "10px",
                    borderRadius: "15px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontSize: "16px",
                  }}
                >
                  Student Name : {terminationData.student_name}
                </Box>
                <Box
                  sx={{
                    backgroundImage: "linear-gradient(to right,#251351,#040926)",
                    px: "20px",
                    py: "10px",
                    borderRadius: "15px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontSize: "16px",
                  }}
                >
                  Course : {terminationData.course_name}
                </Box>
              </Stack>
              <FormikProvider value={formik}>
                <Form onSubmit={formik.handleSubmit}>
                  <Box>
                    <Stack direction={"column"} spacing={2} paddingBottom={2}>
                      <FormControl fullWidth error={formik.touched.course_enrollment_status && Boolean(formik.errors.course_enrollment_status)}>
                        <InputLabel id="course_enrollment_status">Course Enrollment Status</InputLabel>
                        <Select
                          fullWidth
                          label="Course Enrollment Status"
                          name="course_enrollment_status"
                          value={formik.values.course_enrollment_status}
                          onChange={handleChange}
                          onBlur={formik.handleBlur}
                        >
                          <MenuItem value="0">Inactive</MenuItem>
                          <MenuItem value="2">Temporarily Inactive</MenuItem>
                        </Select>
                        {formik.touched.course_enrollment_status && formik.errors.course_enrollment_status && (
                          <FormHelperText>{formik.errors.course_enrollment_status}</FormHelperText>
                        )}
                      </FormControl>

                      {showAlert && (
                        <Alert variant="filled" severity="warning" sx={{ mt: 2 }}>
                          Connect your CRM to issue the TC!
                        </Alert>
                      )}
                    </Stack>
                    <Stack direction={"column"} spacing={2} paddingBottom={2}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          label="Date Of Inactive"
                          sx={{ width: "100%" }}
                          value={formik.values.date_of_inactive ? dayjs(formik.values.date_of_inactive) : null}
                          format="DD-MM-YYYY"
                          onChange={(value) => formik.setFieldValue("date_of_inactive", value ? dayjs(value).format("YYYY-MM-DD") : null)}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: formik.touched.date_of_inactive && Boolean(formik.errors.date_of_inactive),
                              helperText: formik.touched.date_of_inactive && formik.errors.date_of_inactive,
                            },
                          }}
                          maxDate={dayjs()}
                        />
                      </LocalizationProvider>

                      <GlobalSelectField
                        options={{
                          formik,
                          name: "course_inactive_reason",
                          label: "Reason",
                          data: terminationReasons,
                          keys: {
                            id: "course_inactive_reason_id",
                            value: "course_inactive_reason_name",
                          },
                        }}
                      />
                      {formik.values.course_enrollment_status === "2" && (
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            label="Course Restart Date"
                            sx={{ width: "100%" }}
                            value={formik.values.course_restart_date ? dayjs(formik.values.course_restart_date) : null}
                            format="DD-MM-YYYY"
                            onChange={(value) => formik.setFieldValue("course_restart_date", value ? dayjs(value).format("YYYY-MM-DD") : null)}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                error: formik.touched.course_restart_date && Boolean(formik.errors.course_restart_date),
                                helperText: formik.touched.course_restart_date && formik.errors.course_restart_date,
                              },
                            }}
                            minDate={dayjs()}
                          />
                        </LocalizationProvider>
                      )}

                      <TextField
                        fullWidth
                        label="Remarks"
                        name="course_inactive_remarks"
                        placeholder="Remarks"
                        multiline
                        rows={3}
                        value={formik.values.course_inactive_remarks}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.course_inactive_remarks && Boolean(formik.errors.course_inactive_remarks)}
                        helperText={formik.touched.course_inactive_remarks && formik.errors.course_inactive_remarks}
                      />
                    </Stack>
                    {formik.values.course_enrollment_status === "0" && !showAlert && (
                      <Box>
                        <Stack direction={"column"} spacing={2} paddingBottom={2}>
                          <TextField
                            name="student_rating"
                            type="number"
                            value={formik.values.student_rating}
                            error={formik.touched.student_rating && Boolean(formik.errors.student_rating)}
                            helperText={formik.errors.student_rating}
                            onChange={formik.handleChange}
                            fullWidth
                            label="Student Rating"
                          />
                          <TextField
                            fullWidth
                            label="Student Remarks"
                            name="student_remarks"
                            multiline
                            rows={2}
                            value={formik.values.student_remarks}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.student_remarks && Boolean(formik.errors.student_remarks)}
                            helperText={formik.touched.student_remarks && formik.errors.student_remarks}
                          />
                          <TextField
                            name="parent_rating"
                            type="number"
                            value={formik.values.parent_rating}
                            error={formik.touched.parent_rating && Boolean(formik.errors.parent_rating)}
                            helperText={formik.errors.parent_rating}
                            onChange={formik.handleChange}
                            fullWidth
                            label="Parent Rating"
                          />
                          <TextField
                            fullWidth
                            label="Parent Remarks"
                            name="parent_remarks"
                            multiline
                            rows={2}
                            value={formik.values.parent_remarks}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.parent_remarks && Boolean(formik.errors.parent_remarks)}
                            helperText={formik.touched.parent_remarks && formik.errors.parent_remarks}
                          />
                          <TextField
                            name="tutor_rating"
                            type="number"
                            value={formik.values.tutor_rating}
                            error={formik.touched.tutor_rating && Boolean(formik.errors.tutor_rating)}
                            helperText={formik.errors.tutor_rating}
                            onChange={formik.handleChange}
                            fullWidth
                            label="Tutor Rating"
                          />
                          <TextField
                            fullWidth
                            label="Tutor Remarks"
                            name="tutor_remarks"
                            multiline
                            rows={2}
                            value={formik.values.tutor_remarks}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.tutor_remarks && Boolean(formik.errors.tutor_remarks)}
                            helperText={formik.touched.tutor_remarks && formik.errors.tutor_remarks}
                          />
                        </Stack>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 2,
                            flexDirection: "row",
                            flexWrap: "wrap",
                            padding: 2,
                          }}
                        >
                          <FormControl>
                            <FormLabel sx={{ fontSize: 14 }}>Admission Fee</FormLabel>
                            <RadioGroup row name="admission_fee" value={formik.values.admission_fee} onChange={formik.handleChange}>
                              <FormControlLabel value="1" control={<Radio />} label="Paid" />
                              <FormControlLabel value="0" control={<Radio />} label="Not Paid" />
                            </RadioGroup>
                          </FormControl>
                          <FormControl>
                            <FormLabel sx={{ fontSize: 14 }}>Course Fee</FormLabel>
                            <RadioGroup row name="course_fee" value={formik.values.course_fee} onChange={formik.handleChange}>
                              <FormControlLabel value="1" control={<Radio />} label="Paid" />
                              <FormControlLabel value="0" control={<Radio />} label="Not Paid" />
                            </RadioGroup>
                          </FormControl>
                          <FormControl>
                            <FormLabel sx={{ fontSize: 14 }}>Certificate</FormLabel>
                            <RadioGroup row name="certificate" value={formik.values.certificate} onChange={formik.handleChange}>
                              <FormControlLabel value="1" control={<Radio />} label="Issued" disabled />
                              <FormControlLabel value="0" control={<Radio />} label="Not Issued" disabled />
                            </RadioGroup>
                          </FormControl>
                          <FormControl>
                            <FormLabel sx={{ fontSize: 14 }}>Parent Freebie</FormLabel>
                            <RadioGroup row name="parent_freebie" value={formik.values.parent_freebie} onChange={formik.handleChange}>
                              <FormControlLabel value="1" control={<Radio />} label="Given" />
                              <FormControlLabel value="0" control={<Radio />} label="Not Given" />
                            </RadioGroup>
                          </FormControl>
                          <FormControl>
                            <FormLabel sx={{ fontSize: 14 }}>Student Freebie</FormLabel>
                            <RadioGroup row name="student_freebie" value={formik.values.student_freebie} onChange={formik.handleChange}>
                              <FormControlLabel value="1" control={<Radio />} label="Given" />
                              <FormControlLabel value="0" control={<Radio />} label="Not Given" />
                            </RadioGroup>
                          </FormControl>
                          <FormControl>
                            <FormLabel sx={{ fontSize: 14 }}>CRO Retain call</FormLabel>
                            <RadioGroup row name="cro_retain_call" value={formik.values.cro_retain_call} onChange={formik.handleChange}>
                              <FormControlLabel value="1" control={<Radio />} label="Called" />
                              <FormControlLabel value="0" control={<Radio />} label="Not Called" />
                            </RadioGroup>
                          </FormControl>
                          <FormControl>
                            <FormLabel sx={{ fontSize: 14 }}>OGA Retain call</FormLabel>
                            <RadioGroup row name="sro_retain_call" value={formik.values.sro_retain_call} onChange={formik.handleChange}>
                              <FormControlLabel value="1" control={<Radio />} label="Called" />
                              <FormControlLabel value="0" control={<Radio />} label="Not Called" />
                            </RadioGroup>
                          </FormControl>
                          <FormControl>
                            <FormLabel sx={{ fontSize: 14 }}>CRO Exit call</FormLabel>
                            <RadioGroup row name="cro_exit_call" value={formik.values.cro_exit_call} onChange={formik.handleChange}>
                              <FormControlLabel value="1" control={<Radio />} label="Called" />
                              <FormControlLabel value="0" control={<Radio />} label="Not Called" />
                            </RadioGroup>
                          </FormControl>
                        </Box>
                      </Box>
                    )}
                    <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 2 }}>
                      <Stack direction={"row"} spacing={1}>
                        <CancelButton size="small" action={() => setOpen(false)}>
                          Cancel
                        </CancelButton>
                        <SubmitButton disabled={LOADING} size="small" type="submit">
                          {LOADING ? "Please wait..." : "Save"}
                        </SubmitButton>
                      </Stack>
                    </Box>
                  </Box>
                </Form>
              </FormikProvider>
            </>
          )
        ) : null
      }
    />
  );
};

export default AdmissionStatusLanding;
